import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SlidersHorizontal, Timer, Volume2, ImageIcon, Save, Upload, X, Play, History, Database, Download, RotateCcw, AlertTriangle } from 'lucide-react';
import { VERSION_HISTORY, formatFrenchDate } from '../../version';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const TABS = [
  { id: 'event', label: 'Paramètres événement', icon: Timer },
  { id: 'voice', label: 'Sons vocaux', icon: Volume2 },
  { id: 'logos', label: "Logos d'agence", icon: ImageIcon },
  { id: 'versions', label: 'Versions', icon: History },
];

const AGENCIES = [
  { key: 'AIDA', label: 'AIDA International' },
  { key: 'CMAS', label: 'CMAS Monde' },
  { key: 'CMAS_CANADA', label: 'CMAS Canada' },
];

const GlobalSettingsDialog = ({ open, onClose, settings, onSaved, t = (k, fb) => fb }) => {
  const [activeTab, setActiveTab] = useState('event');
  const [eventForm, setEventForm] = useState({});
  const [voiceForm, setVoiceForm] = useState({});
  const [logos, setLogos] = useState({ AIDA: '', CMAS: '', CMAS_CANADA: '' });
  const [saving, setSaving] = useState(false);
  const [voices, setVoices] = useState([]);
  // Backup state (Versions tab)
  const [backupStats, setBackupStats] = useState(null);
  const [backupStatsLoading, setBackupStatsLoading] = useState(false);
  const [backupDownloading, setBackupDownloading] = useState(false);
  const [backupFeedback, setBackupFeedback] = useState(null);
  // Restore state (Versions tab — 2-step verification)
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreparing, setRestorePreparing] = useState(false);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreCodeInput, setRestoreCodeInput] = useState('');
  const [restoreConfirmChecked, setRestoreConfirmChecked] = useState(false);
  const [restoreConfirming, setRestoreConfirming] = useState(false);
  const [restoreFeedback, setRestoreFeedback] = useState(null);
  const [restoreHistory, setRestoreHistory] = useState([]);
  const restoreFileRef = useRef(null);
  const fileRefs = useRef({ AIDA: null, CMAS: null, CMAS_CANADA: null });

  useEffect(() => {
    if (!open || !settings) return;
    setEventForm({
      duration_per_passage_minutes: settings.event_parameters?.duration_per_passage_minutes ?? 6,
      briefing_duration_minutes: settings.event_parameters?.briefing_duration_minutes ?? 15,
      warmup_duration_minutes: settings.event_parameters?.warmup_duration_minutes ?? 45,
      official_zone_duration_minutes: settings.event_parameters?.official_zone_duration_minutes ?? 2,
      duration_before_protest_minutes: settings.event_parameters?.duration_before_protest_minutes ?? 15,
      protest_time_duration_minutes: settings.event_parameters?.protest_time_duration_minutes ?? 15,
      event_live_simulation: !!settings.event_live_simulation,
    });
    setVoiceForm({
      enabled: settings.voice_settings?.enabled ?? true,
      speak_during_ventilation: settings.voice_settings?.speak_during_ventilation ?? true,
      speak_during_apnea: settings.voice_settings?.speak_during_apnea ?? true,
      voice_lang: settings.voice_settings?.voice_lang ?? 'fr-FR',
      voice_name: settings.voice_settings?.voice_name ?? '',
      rate: settings.voice_settings?.rate ?? 1.0,
      pitch: settings.voice_settings?.pitch ?? 1.0,
      volume: settings.voice_settings?.volume ?? 1.0,
      text_long_countdown: settings.voice_settings?.text_long_countdown ?? '{n} secondes',
      text_final_countdown: settings.voice_settings?.text_final_countdown ?? '{n}',
      text_start_apnea: settings.voice_settings?.text_start_apnea ?? "Commencer l'apnée",
      text_start_ventilation: settings.voice_settings?.text_start_ventilation ?? 'Respirez',
      text_table_complete: settings.voice_settings?.text_table_complete ?? 'Table terminée. Bravo !',
    });
    setLogos({
      AIDA: settings.agency_logos?.AIDA ?? '',
      CMAS: settings.agency_logos?.CMAS ?? '',
      CMAS_CANADA: settings.agency_logos?.CMAS_CANADA ?? '',
    });
  }, [open, settings]);

  // Fetch system voices
  useEffect(() => {
    if (!open || typeof window === 'undefined' || !window.speechSynthesis) return;
    const loadVoices = () => {
      const v = window.speechSynthesis.getVoices();
      if (v && v.length) setVoices(v);
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, [open]);

  const handleTestVoice = (text) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new window.SpeechSynthesisUtterance(text || 'Test de voix');
    utt.lang = voiceForm.voice_lang || 'fr-FR';
    utt.rate = parseFloat(voiceForm.rate) || 1.0;
    utt.pitch = parseFloat(voiceForm.pitch) || 1.0;
    utt.volume = parseFloat(voiceForm.volume) || 1.0;
    if (voiceForm.voice_name) {
      const match = voices.find((v) => v.name === voiceForm.voice_name);
      if (match) utt.voice = match;
    }
    window.speechSynthesis.speak(utt);
  };

  const handleLogoUpload = async (agency, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Fichier trop volumineux (max 2 MB)');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('agency', agency);
      fd.append('file', file);
      const res = await axios.post(`${API_BASE_URL}/api/settings/agency-logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Re-fetch settings to get the updated data URL
      const sres = await axios.get(`${API_BASE_URL}/api/settings`);
      setLogos({
        AIDA: sres.data.agency_logos?.AIDA ?? '',
        CMAS: sres.data.agency_logos?.CMAS ?? '',
        CMAS_CANADA: sres.data.agency_logos?.CMAS_CANADA ?? '',
      });
      if (onSaved) onSaved(sres.data);
    } catch (err) {
      alert(`Erreur upload: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleLogoClear = async (agency) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/settings/agency-logo/${agency}`);
      setLogos((prev) => ({ ...prev, [agency]: '' }));
      const sres = await axios.get(`${API_BASE_URL}/api/settings`);
      if (onSaved) onSaved(sres.data);
    } catch (err) {
      alert(`Erreur: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        event_parameters: {
          duration_per_passage_minutes: parseInt(eventForm.duration_per_passage_minutes) || 6,
          briefing_duration_minutes: parseInt(eventForm.briefing_duration_minutes) || 0,
          warmup_duration_minutes: parseInt(eventForm.warmup_duration_minutes) || 0,
          official_zone_duration_minutes: parseInt(eventForm.official_zone_duration_minutes) || 0,
          duration_before_protest_minutes: parseInt(eventForm.duration_before_protest_minutes) || 0,
          protest_time_duration_minutes: parseInt(eventForm.protest_time_duration_minutes) || 0,
        },
        event_live_simulation: !!eventForm.event_live_simulation,
        voice_settings: {
          enabled: !!voiceForm.enabled,
          speak_during_ventilation: !!voiceForm.speak_during_ventilation,
          speak_during_apnea: !!voiceForm.speak_during_apnea,
          voice_lang: voiceForm.voice_lang,
          voice_name: voiceForm.voice_name,
          rate: parseFloat(voiceForm.rate) || 1.0,
          pitch: parseFloat(voiceForm.pitch) || 1.0,
          volume: parseFloat(voiceForm.volume) || 1.0,
          text_long_countdown: voiceForm.text_long_countdown,
          text_final_countdown: voiceForm.text_final_countdown,
          text_start_apnea: voiceForm.text_start_apnea,
          text_start_ventilation: voiceForm.text_start_ventilation,
          text_table_complete: voiceForm.text_table_complete,
        },
      };
      const res = await axios.put(`${API_BASE_URL}/api/settings`, payload);
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      alert(`Erreur: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Auto-load backup stats when Versions tab is active
  useEffect(() => {
    if (!open || activeTab !== 'versions') return;
    let cancelled = false;
    const loadStats = async () => {
      setBackupStatsLoading(true);
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
        const res = await axios.get(`${API_BASE_URL}/api/admin/backup/stats`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setBackupStats(res.data);
      } catch (e) {
        if (!cancelled) setBackupStats({ error: e.response?.data?.detail || 'Stats indisponibles' });
      } finally {
        if (!cancelled) setBackupStatsLoading(false);
      }
    };
    const loadHistory = async () => {
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
        const res = await axios.get(`${API_BASE_URL}/api/admin/backup/restore/history`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setRestoreHistory(res.data?.history || []);
      } catch (e) {
        if (!cancelled) setRestoreHistory([]);
      }
    };
    loadStats();
    loadHistory();
    return () => { cancelled = true; };
  }, [open, activeTab]);

  const handleDownloadBackup = async () => {
    setBackupDownloading(true);
    setBackupFeedback(null);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      const res = await axios.get(`${API_BASE_URL}/api/admin/backup`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      // Pretty-print JSON for human readability
      const json = JSON.stringify(res.data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.href = url;
      link.download = `apnea-coach-backup-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      const totalDocs = res.data?.metadata?.total_documents || 0;
      setBackupFeedback({
        type: 'success',
        message: `✓ Backup téléchargé (${totalDocs} documents sur ${Object.keys(res.data?.collections || {}).length} collections).`,
      });
    } catch (err) {
      setBackupFeedback({
        type: 'error',
        message: `Erreur : ${err.response?.data?.detail || err.message}`,
      });
    } finally {
      setBackupDownloading(false);
    }
  };

  const resetRestore = () => {
    setRestoreFile(null);
    setRestorePreview(null);
    setRestoreCodeInput('');
    setRestoreConfirmChecked(false);
    setRestoreFeedback(null);
    if (restoreFileRef.current) restoreFileRef.current.value = '';
  };

  const handleRestoreFileSelected = async (file) => {
    if (!file) return;
    setRestoreFeedback(null);
    setRestorePreview(null);
    setRestoreCodeInput('');
    setRestoreConfirmChecked(false);
    setRestoreFile(file);
    setRestorePreparing(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_BASE_URL}/api/admin/backup/restore/prepare`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      setRestorePreview(res.data);
    } catch (err) {
      setRestoreFeedback({ type: 'error', message: `Erreur : ${err.response?.data?.detail || err.message}` });
      setRestoreFile(null);
      if (restoreFileRef.current) restoreFileRef.current.value = '';
    } finally {
      setRestorePreparing(false);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!restorePreview) return;
    setRestoreConfirming(true);
    setRestoreFeedback(null);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      const res = await axios.post(
        `${API_BASE_URL}/api/admin/backup/restore/confirm`,
        { token: restorePreview.token, confirmation_code: restoreCodeInput.trim() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setRestoreFeedback({
        type: 'success',
        message: `✓ Restauration terminée : ${res.data.total_documents_restored} documents restaurés.${res.data.snapshot_saved ? ' Un instantané de sécurité a été créé.' : ''} Rechargement de l'application…`,
      });
      setRestorePreview(null);
      setRestoreFile(null);
      setRestoreCodeInput('');
      setRestoreConfirmChecked(false);
      if (restoreFileRef.current) restoreFileRef.current.value = '';
      // Auto-refresh the whole app so restored data is reflected everywhere
      setTimeout(() => {
        if (typeof window !== 'undefined') window.location.reload();
      }, 2200);
      try {
        const sres = await axios.get(`${API_BASE_URL}/api/admin/backup/stats`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setBackupStats(sres.data);
      } catch (e) {}
    } catch (err) {
      setRestoreFeedback({ type: 'error', message: `Erreur : ${err.response?.data?.detail || err.message}` });
    } finally {
      setRestoreConfirming(false);
    }
  };

  const codeMatches =
    !!restorePreview &&
    restoreCodeInput.trim().toUpperCase() === (restorePreview.confirmation_code || '').toUpperCase();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-2xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="global-settings-modal">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-blue-200 flex items-center">
            <SlidersHorizontal size={24} className="mr-2 text-teal-400" />
            {t('globalSettings', 'Paramètres globaux')}
          </DialogTitle>
          <DialogDescription className="text-blue-300 text-xs">
            {t('globalSettingsDesc', 'Configuration de l\'application')}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-blue-700/40 shrink-0">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === tab.id ? 'border-teal-400 text-teal-200' : 'border-transparent text-blue-400 hover:text-blue-200'
                }`}
                data-testid={`settings-tab-${tab.id}`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="overflow-y-auto flex-1 p-1">
          {activeTab === 'event' && (
            <div className="space-y-4 p-1">
              <div className="bg-blue-800/30 rounded-lg p-4">
                <h3 className="text-teal-300 font-semibold mb-3 flex items-center">
                  <Timer size={16} className="mr-2" />
                  Paramètres d'événement
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { k: 'duration_per_passage_minutes', l: 'Durée par passage (min)', tid: 'setting-duration-per-passage' },
                    { k: 'briefing_duration_minutes', l: 'Durée briefing (min)', tid: 'setting-briefing-duration' },
                    { k: 'warmup_duration_minutes', l: 'Durée échauffement (min)', tid: 'setting-warmup-duration' },
                    { k: 'official_zone_duration_minutes', l: 'Zone officielle (min)', tid: 'setting-official-zone-duration' },
                    { k: 'duration_before_protest_minutes', l: 'Avant protestation (min)', tid: 'setting-before-protest-duration' },
                    { k: 'protest_time_duration_minutes', l: 'Durée protestation (min)', tid: 'setting-protest-duration' },
                  ].map(({ k, l, tid }) => (
                    <div key={k}>
                      <Label className="text-blue-200 text-sm">{l}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={eventForm[k] ?? ''}
                        onChange={(e) => setEventForm((p) => ({ ...p, [k]: e.target.value }))}
                        className="bg-blue-800/50 border-blue-600 text-white mt-1"
                        data-testid={tid}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-orange-900/20 rounded-lg p-4 border border-orange-700/30">
                <label className="flex items-center gap-3 cursor-pointer" data-testid="simulation-mode-toggle">
                  <input
                    type="checkbox"
                    checked={eventForm.event_live_simulation || false}
                    onChange={(e) => setEventForm((p) => ({ ...p, event_live_simulation: e.target.checked }))}
                    className="w-5 h-5 rounded border-orange-600 bg-orange-900/50 accent-orange-500"
                  />
                  <div>
                    <span className="text-orange-200 font-semibold">Mode simulation événement live</span>
                    <p className="text-orange-400 text-xs mt-0.5">Active le mode simulation avec skip-to-next pour tester les événements live</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'voice' && (
            <div className="space-y-4 p-1">
              <div className="bg-blue-800/30 rounded-lg p-4 space-y-3">
                <h3 className="text-teal-300 font-semibold flex items-center">
                  <Volume2 size={16} className="mr-2" />
                  Annonces vocales (Tables d'apnée)
                </h3>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={voiceForm.enabled || false}
                    onChange={(e) => setVoiceForm((p) => ({ ...p, enabled: e.target.checked }))}
                    className="w-5 h-5 accent-teal-500"
                    data-testid="voice-enabled-toggle"
                  />
                  <span className="text-blue-200 font-medium">Activer les annonces vocales</span>
                </label>
                <div className="grid grid-cols-2 gap-3 pl-8">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={voiceForm.speak_during_ventilation || false}
                      onChange={(e) => setVoiceForm((p) => ({ ...p, speak_during_ventilation: e.target.checked }))}
                      disabled={!voiceForm.enabled}
                      className="accent-green-500"
                      data-testid="voice-ventilation-toggle"
                    />
                    <span className="text-green-300 text-sm">🟢 Pendant la ventilation</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={voiceForm.speak_during_apnea || false}
                      onChange={(e) => setVoiceForm((p) => ({ ...p, speak_during_apnea: e.target.checked }))}
                      disabled={!voiceForm.enabled}
                      className="accent-red-500"
                      data-testid="voice-apnea-toggle"
                    />
                    <span className="text-red-300 text-sm">🔴 Pendant l'apnée</span>
                  </label>
                </div>
              </div>

              <div className="bg-blue-800/30 rounded-lg p-4 space-y-3">
                <h4 className="text-teal-300 font-semibold text-sm">Voix du navigateur</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-blue-200 text-xs">Langue</Label>
                    <select
                      value={voiceForm.voice_lang || 'fr-FR'}
                      onChange={(e) => setVoiceForm((p) => ({ ...p, voice_lang: e.target.value }))}
                      className="w-full bg-blue-800/50 border border-blue-600 text-white mt-1 rounded h-9 px-2 text-sm"
                      data-testid="voice-lang-select"
                    >
                      <option value="fr-FR">Français</option>
                      <option value="en-US">English (US)</option>
                      <option value="en-GB">English (UK)</option>
                      <option value="es-ES">Español</option>
                      <option value="de-DE">Deutsch</option>
                      <option value="it-IT">Italiano</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-blue-200 text-xs">Voix (optionnelle)</Label>
                    <select
                      value={voiceForm.voice_name || ''}
                      onChange={(e) => setVoiceForm((p) => ({ ...p, voice_name: e.target.value }))}
                      className="w-full bg-blue-800/50 border border-blue-600 text-white mt-1 rounded h-9 px-2 text-sm"
                      data-testid="voice-name-select"
                    >
                      <option value="">-- Voix par défaut --</option>
                      {voices
                        .filter((v) => !voiceForm.voice_lang || v.lang.startsWith(voiceForm.voice_lang.slice(0, 2)))
                        .map((v) => (
                          <option key={v.name} value={v.name}>{v.name} ({v.lang})</option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { k: 'rate', l: 'Vitesse', min: 0.5, max: 2.0, step: 0.1 },
                    { k: 'pitch', l: 'Hauteur', min: 0.5, max: 2.0, step: 0.1 },
                    { k: 'volume', l: 'Volume', min: 0.0, max: 1.0, step: 0.1 },
                  ].map(({ k, l, min, max, step }) => (
                    <div key={k}>
                      <Label className="text-blue-200 text-xs">{l} ({voiceForm[k]})</Label>
                      <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={voiceForm[k] || 1.0}
                        onChange={(e) => setVoiceForm((p) => ({ ...p, [k]: parseFloat(e.target.value) }))}
                        className="w-full mt-1 accent-teal-500"
                      />
                    </div>
                  ))}
                </div>
                <Button onClick={() => handleTestVoice('Bonjour, ceci est un test')} variant="outline" className="border-teal-500 text-teal-200 hover:bg-teal-700/50 w-full" data-testid="voice-test-btn">
                  <Play className="mr-2" size={14} />
                  Tester la voix
                </Button>
              </div>

              <div className="bg-blue-800/30 rounded-lg p-4 space-y-3">
                <h4 className="text-teal-300 font-semibold text-sm">Textes des annonces</h4>
                <p className="text-blue-400 text-[11px]">Utilisez {'{n}'} comme placeholder pour le nombre de secondes.</p>
                {[
                  { k: 'text_long_countdown', l: 'Décompte long (60/30/15/10s)' },
                  { k: 'text_final_countdown', l: 'Décompte final (5,4,3,2,1)' },
                  { k: 'text_start_apnea', l: "Début d'apnée" },
                  { k: 'text_start_ventilation', l: 'Début de ventilation' },
                  { k: 'text_table_complete', l: 'Fin de table' },
                ].map(({ k, l }) => (
                  <div key={k} className="grid grid-cols-[1fr_auto] gap-2 items-end">
                    <div>
                      <Label className="text-blue-200 text-xs">{l}</Label>
                      <Input
                        value={voiceForm[k] || ''}
                        onChange={(e) => setVoiceForm((p) => ({ ...p, [k]: e.target.value }))}
                        className="bg-blue-800/50 border-blue-600 text-white mt-1 h-9 text-sm"
                        data-testid={`voice-text-${k}`}
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={() => handleTestVoice((voiceForm[k] || '').replace('{n}', '5'))}
                      variant="outline"
                      className="border-teal-500/50 text-teal-300 h-9 px-2"
                      data-testid={`voice-test-${k}`}
                    >
                      <Play size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'logos' && (
            <div className="space-y-4 p-1">
              <div className="bg-blue-800/30 rounded-lg p-4 space-y-3">
                <h3 className="text-teal-300 font-semibold flex items-center">
                  <ImageIcon size={16} className="mr-2" />
                  Logos d'agences (apparaissent sur les PDFs)
                </h3>
                <p className="text-blue-400 text-xs">Format : PNG, JPEG, SVG, WebP — Taille max : 2 MB. Laissez vide pour utiliser le logo par défaut.</p>
                {AGENCIES.map(({ key, label }) => (
                  <div key={key} className="bg-blue-950/40 rounded-lg p-3 flex items-center gap-3" data-testid={`agency-logo-${key}`}>
                    <div className="w-20 h-20 bg-white/10 rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
                      {logos[key] ? (
                        <img src={logos[key]} alt={label} className="max-w-full max-h-full object-contain" />
                      ) : (
                        <ImageIcon size={32} className="text-blue-500 opacity-50" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-blue-100 font-medium">{label}</div>
                      <div className="text-blue-400 text-xs mb-2">
                        {logos[key] ? 'Logo personnalisé chargé' : 'Logo par défaut utilisé'}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <input
                          type="file"
                          ref={(el) => (fileRefs.current[key] = el)}
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                          onChange={(e) => handleLogoUpload(key, e.target.files[0])}
                          className="hidden"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fileRefs.current[key]?.click()}
                          className="border-teal-500/50 text-teal-200 hover:bg-teal-800/40 h-8"
                          data-testid={`upload-logo-${key}`}
                        >
                          <Upload size={12} className="mr-1" />
                          {logos[key] ? 'Remplacer' : 'Charger'}
                        </Button>
                        {logos[key] && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleLogoClear(key)}
                            className="border-red-500/50 text-red-300 hover:bg-red-800/40 h-8"
                            data-testid={`clear-logo-${key}`}
                          >
                            <X size={12} className="mr-1" />
                            Réinitialiser
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'versions' && (
            <div className="space-y-3 p-1" data-testid="versions-tab-content">
              {/* Backup section */}
              <div className="bg-teal-900/30 rounded-lg p-4 border border-teal-700/40">
                <h3 className="text-teal-300 font-semibold mb-1 flex items-center">
                  <Database size={16} className="mr-2" />
                  Sauvegarde des données
                </h3>
                <p className="text-blue-400 text-xs mb-3">
                  Téléchargez un instantané JSON complet de la base avant chaque redéploiement.
                </p>
                {backupStatsLoading ? (
                  <div className="text-blue-400 text-xs animate-pulse mb-3">Chargement des statistiques…</div>
                ) : backupStats?.error ? (
                  <div className="text-red-300 text-xs mb-3">{backupStats.error}</div>
                ) : backupStats ? (
                  <div className="grid grid-cols-2 gap-2 mb-3" data-testid="backup-stats">
                    <div className="bg-blue-950/40 rounded p-2 text-center">
                      <div className="text-2xl font-bold text-cyan-300">{backupStats.total_documents}</div>
                      <div className="text-[10px] text-blue-400 uppercase">Documents</div>
                    </div>
                    <div className="bg-blue-950/40 rounded p-2 text-center">
                      <div className="text-2xl font-bold text-cyan-300">
                        {Object.keys(backupStats.collections || {}).length}
                      </div>
                      <div className="text-[10px] text-blue-400 uppercase">Collections</div>
                    </div>
                  </div>
                ) : null}
                <Button
                  onClick={handleDownloadBackup}
                  disabled={backupDownloading}
                  className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 w-full sm:w-auto"
                  data-testid="download-backup-btn"
                >
                  {backupDownloading ? (
                    <span className="animate-pulse">Préparation du backup…</span>
                  ) : (
                    <>
                      <Download className="mr-2" size={16} />
                      Télécharger un backup
                    </>
                  )}
                </Button>
                {backupFeedback && (
                  <div
                    className={`mt-2 text-xs px-2 py-1.5 rounded ${
                      backupFeedback.type === 'success'
                        ? 'bg-green-900/40 text-green-200 border border-green-700/40'
                        : 'bg-red-900/40 text-red-200 border border-red-700/40'
                    }`}
                    data-testid="backup-feedback"
                  >
                    {backupFeedback.message}
                  </div>
                )}
              </div>

              {/* Restore section — 2-step verification */}
              <div className="bg-red-950/30 rounded-lg p-4 border border-red-800/50" data-testid="restore-backup-section">
                <h3 className="text-red-300 font-semibold mb-1 flex items-center">
                  <RotateCcw size={16} className="mr-2" />
                  Restaurer un backup
                </h3>
                <p className="text-red-200/70 text-xs mb-3 flex items-start gap-1.5">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  <span>Action destructive : les données actuelles seront <strong>écrasées</strong> par le contenu du fichier. Un instantané de sécurité est créé automatiquement avant l'opération.</span>
                </p>

                {!restorePreview ? (
                  <>
                    <input
                      type="file"
                      ref={restoreFileRef}
                      accept="application/json,.json"
                      onChange={(e) => handleRestoreFileSelected(e.target.files?.[0])}
                      className="hidden"
                      data-testid="restore-file-input"
                    />
                    <Button
                      onClick={() => restoreFileRef.current?.click()}
                      disabled={restorePreparing}
                      variant="outline"
                      className="border-red-600/60 text-red-200 hover:bg-red-900/40 w-full sm:w-auto"
                      data-testid="restore-choose-file-btn"
                    >
                      {restorePreparing ? (
                        <span className="animate-pulse">Analyse du fichier…</span>
                      ) : (
                        <>
                          <Upload className="mr-2" size={16} />
                          Choisir un fichier .json
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <div className="space-y-3" data-testid="restore-preview">
                    <div className="bg-blue-950/50 rounded-lg p-3 text-xs">
                      <div className="text-blue-200 font-medium mb-2 truncate">📄 {restorePreview.filename}</div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="bg-blue-900/40 rounded p-2 text-center">
                          <div className="text-xl font-bold text-cyan-300" data-testid="restore-preview-total">{restorePreview.preview?.total_documents}</div>
                          <div className="text-[10px] text-blue-400 uppercase">Documents</div>
                        </div>
                        <div className="bg-blue-900/40 rounded p-2 text-center">
                          <div className="text-xl font-bold text-cyan-300">{restorePreview.preview?.valid_collections}</div>
                          <div className="text-[10px] text-blue-400 uppercase">Collections</div>
                        </div>
                      </div>
                      {restorePreview.preview?.backup_exported_at && (
                        <div className="text-blue-400 text-[11px]">
                          Exporté le : {new Date(restorePreview.preview.backup_exported_at).toLocaleString('fr-FR')}
                        </div>
                      )}
                    </div>

                    <div className="bg-amber-950/30 border border-amber-700/40 rounded-lg p-3">
                      <p className="text-amber-200 text-xs mb-2">Pour confirmer, recopiez ce code exactement :</p>
                      <div className="text-2xl font-mono font-bold tracking-[0.3em] text-amber-300 text-center bg-amber-950/40 rounded py-2 mb-2 select-all" data-testid="restore-confirmation-code">
                        {restorePreview.confirmation_code}
                      </div>
                      <Input
                        value={restoreCodeInput}
                        onChange={(e) => setRestoreCodeInput(e.target.value.toUpperCase())}
                        placeholder="Recopiez le code ici"
                        maxLength={6}
                        className="bg-blue-950/60 border-amber-700/50 text-white text-center font-mono tracking-[0.3em] uppercase"
                        data-testid="restore-code-input"
                      />
                    </div>

                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={restoreConfirmChecked}
                        onChange={(e) => setRestoreConfirmChecked(e.target.checked)}
                        className="mt-0.5 accent-red-500"
                        data-testid="restore-confirm-checkbox"
                      />
                      <span className="text-red-200 text-xs">Je comprends que cette action est irréversible et écrasera toutes les données actuelles.</span>
                    </label>

                    <div className="flex gap-2 flex-wrap">
                      <Button
                        onClick={handleRestoreConfirm}
                        disabled={!codeMatches || !restoreConfirmChecked || restoreConfirming}
                        className="bg-red-600 hover:bg-red-700 disabled:opacity-40"
                        data-testid="restore-confirm-btn"
                      >
                        {restoreConfirming ? (
                          <span className="animate-pulse">Restauration…</span>
                        ) : (
                          <>
                            <RotateCcw className="mr-2" size={16} />
                            Restaurer maintenant
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={resetRestore}
                        variant="outline"
                        className="border-blue-500 text-blue-200"
                        data-testid="restore-cancel-btn"
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}

                {restoreFeedback && (
                  <div
                    className={`mt-2 text-xs px-2 py-1.5 rounded ${
                      restoreFeedback.type === 'success'
                        ? 'bg-green-900/40 text-green-200 border border-green-700/40'
                        : 'bg-red-900/40 text-red-200 border border-red-700/40'
                    }`}
                    data-testid="restore-feedback"
                  >
                    {restoreFeedback.message}
                  </div>
                )}

                {restoreHistory.length > 0 && (
                  <div className="mt-3 border-t border-red-800/40 pt-3" data-testid="restore-history">
                    <div className="text-red-200/80 text-[11px] uppercase tracking-wider font-semibold mb-2">
                      Dernières restaurations
                    </div>
                    <div className="space-y-1.5">
                      {restoreHistory.slice(0, 5).map((h) => (
                        <div
                          key={h.id}
                          className="flex items-center justify-between gap-2 bg-blue-950/40 rounded px-2 py-1.5 text-[11px]"
                          data-testid={`restore-history-row-${h.id}`}
                        >
                          <div className="min-w-0">
                            <div className="text-blue-200 truncate">
                              {h.restored_at ? new Date(h.restored_at).toLocaleString('fr-FR') : '—'}
                            </div>
                            <div className="text-blue-400 truncate">
                              par {h.restored_by || '—'}{h.filename ? ` · ${h.filename}` : ''}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-cyan-300 font-mono font-bold">{h.total_documents}</span>
                            <span className="text-blue-400"> docs</span>
                            {h.snapshot_saved && <div className="text-green-400/70 text-[10px]">✓ snapshot</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-blue-800/30 rounded-lg p-4">
                <h3 className="text-teal-300 font-semibold mb-1 flex items-center">
                  <History size={16} className="mr-2" />
                  Historique des versions déployées
                </h3>
                <p className="text-blue-400 text-xs">Liste des versions de l'application avec date de déploiement et description des correctifs et ajouts.</p>
              </div>
              <div className="space-y-2">
                {VERSION_HISTORY.map((v, idx) => (
                  <div
                    key={v.version}
                    className={`rounded-lg p-3 border ${idx === 0 ? 'bg-cyan-900/30 border-cyan-600/40' : 'bg-blue-900/30 border-blue-700/40'}`}
                    data-testid={`version-row-${v.version}`}
                  >
                    <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-mono font-bold text-sm ${idx === 0 ? 'text-cyan-200' : 'text-blue-200'}`}>
                          {v.label || `v${v.version}`}
                        </span>
                        <span className="text-blue-300 text-sm">— {v.name}</span>
                        {idx === 0 && (
                          <span className="bg-cyan-500/30 text-cyan-200 border border-cyan-400/40 text-[10px] px-2 py-0.5 rounded-full">
                            ACTUELLE
                          </span>
                        )}
                      </div>
                      <span className="text-teal-300 text-xs whitespace-nowrap">
                        {formatFrenchDate(v.release_date)}
                      </span>
                    </div>
                    <ul className="text-blue-300 text-xs space-y-1 ml-1">
                      {(v.highlights || []).map((h, hi) => (
                        <li key={hi} className="flex items-start gap-2">
                          <span className="text-cyan-400 mt-0.5 shrink-0">›</span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 pt-3 border-t border-blue-700/40">
          <Button type="button" variant="outline" onClick={onClose} className="border-blue-500 text-blue-200">
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-settings-button">
            {saving ? 'Enregistrement...' : (
              <>
                <Save className="mr-2" size={16} />
                Enregistrer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSettingsDialog;
